import React, { useContext, useEffect, useState, Fragment } from 'react';

import { ComponentContext } from '../../context/componentContext';
import { AuthContext } from '../../context/authContext';
import { OrderContext } from '../../context/orderContext';
import { ProductContext } from '../../context/productContext';

import OrderCard from '../../components/Orders/OrderCard';
import UnexpectedError from '../../components/UnexpectedError';
import AvailableProduct from '../../components/AvailableProduct';

//images
import Illustration from '../../images/farm-illustration.svg';
import { Flex, Box, Heading, Image } from '@chakra-ui/core';

const Orders = ({ history }) => {
  document.title = 'CrowdFarmer | Orders';

  const { reload, unexpectedError, setUnexpectedError, setLoading } = useContext(ComponentContext);
  const { session, setSession, isAuthenticated } = useContext(AuthContext);
  const { getOrders } = useContext(OrderContext);
  const { getProducts } = useContext(ProductContext);

  const [orders, setOrders] = useState([]);
  const [deletedOrder, setDeletedOrder] = useState(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [products, setProducts] = useState([]);
  const [currentCompletePage, setCurrentCompletePage] = useState(1);
  const [currentPendingPage, setCurrentPendingPage] = useState(1);

  const handleCompleteClick = e => {
    setCurrentCompletePage(Number(e.target.id));
  };

  const handlePendingClick = e => {
    setCurrentPendingPage(Number(e.target.id));
  };

  useEffect(() => {
    let mounted = true;
    if (session)
      (async () => {
        setLoading(true);
        try {
          const result = await getOrders();
          if (mounted)
            if (result.code === 200) {
              if (result.data.length === 0) {
                let products = await getProducts({ available: true });
                if (products.code === 200) {
                  if (products.data.length > 0) {
                    setIsEmpty(false);
                    setProducts(products.data);
                  }
                } else setUnexpectedError('Unexpected Server Error!');
              } else {
                setIsEmpty(false);
                setOrders(result.data);
              }
            } else setUnexpectedError('Unexpected Server Error!');
        } catch (error) {
          if (error && error.status === 401) setSession(false);
          else setUnexpectedError('Unexpected Server Error!');
        }
        setLoading(false);
      })();
    return () => (mounted = false);
  }, [reload, getOrders, session, setLoading, setSession, setUnexpectedError, getProducts]);

  // initialize pagination for completed orders.
  const indexLastCompleteOrder = currentCompletePage * 3;
  const indexFirstCompleteOrder = indexLastCompleteOrder - 3;
  const completeOrders = orders.filter(order => order.paidOn !== undefined || null);
  const currentCompleteOrders = completeOrders.slice(
    indexFirstCompleteOrder,
    indexLastCompleteOrder
  );

  // set page numbers.
  const completePageNumbers = [];
  for (let i = 1; i <= Math.ceil(completeOrders.length / 3); i++) {
    completePageNumbers.push(i);
  }

  // display paginator numbers.
  const renderCompletePageNumbers = completePageNumbers.map((num, index) => {
    return (
      <Flex justify="center" align="center" key={index}>
        <Flex as="ul">
          <Flex
            as="li"
            cursor="pointer"
            w={6}
            h={6}
            rounded="sm"
            align="center"
            justify="center"
            borderColor="cf.400"
            border={num === currentPendingPage && 1}
            bg={num === currentPendingPage && 1 && 'cf.400'}
            color={num === currentPendingPage ? 'white' : 'black'}
            id={num}
            onClick={handleCompleteClick}
          >
            {num}
          </Flex>
        </Flex>
      </Flex>
    );
  });

  // initialize paginator for pending orders.
  const indexLastPendingOrder = currentPendingPage * 3;
  const indexFirstPendingOrder = indexLastPendingOrder - 3;
  let pendingOrders = orders.filter(order => order.paidOn === undefined || null);

  if (deletedOrder) {
    pendingOrders = pendingOrders.filter(order => order._id !== deletedOrder._id);
    //TODO sessionStorage.setItem('deletedOrder', JSON.stringify(deletedOrder))
  }

  const currentPendingOrders = pendingOrders.slice(indexFirstPendingOrder, indexLastPendingOrder);

  // set page numbers.
  const pendingPageNumbers = [];
  for (let i = 1; i <= Math.ceil(pendingOrders.length / 3); i++) {
    pendingPageNumbers.push(i);
  }

  // display paginator numbers
  const renderPendingPageNumbers = pendingPageNumbers.map(num => {
    return (
      <Flex justify="center" align="center" key={num}>
        <Flex as="ul">
          <Flex
            as="li"
            id={num}
            onClick={handlePendingClick}
            cursor="pointer"
            w={6}
            h={6}
            mb={10}
            rounded="sm"
            align="center"
            justify="center"
            borderColor="cf.400"
            border={num === currentPendingPage && 1}
            bg={num === currentPendingPage && 1 && 'cf.400'}
            color={num === currentPendingPage ? 'white' : 'black'}
          >
            {num}
          </Flex>
        </Flex>
      </Flex>
    );
  });

  return (
    <Fragment>
      {unexpectedError ? (
        <UnexpectedError unexpectedError={unexpectedError} />
      ) : (
        <Box pt={40} pb={0}>
          <Fragment>
            <Flex
              wrap="wrap"
              mb={10}
              px={{ lg: 48 }}
              pl={{ base: 14, md: 20 }}
              pr={{ base: 4, md: 5 }}
            >
              <Heading as="h2">Here are your pending and paid orders</Heading>
            </Flex>
            <Image
              w="100%"
              objectFit="center"
              mt={{ base: 6, md: -6 }}
              src={Illustration}
              alt="Maize"
            />
          </Fragment>
          <>
            {isEmpty ? (
              <Box pt={40} className="farms-bg">
                <Flex align="center" justify="center" flexDir="column" mx="auto" w="50%">
                  <Heading as="h2">You have no order yet.</Heading>
                </Flex>
              </Box>
            ) : (
              <>
                {products.length > 0 ? (
                  <AvailableProduct title="order" products={products} />
                ) : (
                  <>
                    {currentPendingOrders.length && (
                      <Box bg="cf.200">
                        <Flex
                          wrap="wrap"
                          align="center"
                          justify="space-between"
                          px={{ lg: 48 }}
                          pl={{ base: 14, md: 20 }}
                          pr={{ base: 4, md: 5 }}
                          pt={16}
                        >
                          <Heading as="h4">Pending Orders</Heading>
                        </Flex>

                        <Box px={{ lg: 48 }} pl={{ base: 14, md: 20 }} pr={{ base: 4, md: 5 }}>
                          {currentPendingOrders.map((order, index) => (
                            <OrderCard
                              key={order._id}
                              percentage={!order.processingPayment ? '50' : '80'}
                              status="pending"
                              setDeletedOrder={setDeletedOrder}
                              order={order}
                              token={isAuthenticated().token}
                              firstName={isAuthenticated().user.firstName}
                              session={session}
                              setSession={setSession}
                              setUnexpectedError={setUnexpectedError}
                            />
                          ))}
                          <Flex align="center" justify="center">
                            {renderPendingPageNumbers}
                          </Flex>
                        </Box>
                      </Box>
                    )}
                    {currentCompleteOrders.length && (
                      <Box bg="cf.300" pb={16}>
                        <Flex
                          wrap="wrap"
                          px={{ lg: 48 }}
                          pl={{ base: 14, md: 20 }}
                          pr={{ base: 4, md: 5 }}
                          pt={10}
                        >
                          <Heading as="h4">Completed Orders</Heading>
                        </Flex>

                        <Box px={{ lg: 48 }} pl={{ base: 14, md: 20 }} pr={{ base: 4, md: 5 }}>
                          {currentCompleteOrders.map((order, index) => (
                            <Box key={order._id}>
                              <OrderCard
                                percentage="100"
                                status="completed"
                                order={order}
                                token={isAuthenticated().token}
                                firstName={isAuthenticated().user.firstName}
                                session={session}
                                setSession={setSession}
                                setUnexpectedError={setUnexpectedError}
                              />
                            </Box>
                          ))}
                          <Flex align="center" justify="center">
                            {renderCompletePageNumbers}
                          </Flex>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </>
        </Box>
      )}
    </Fragment>
  );
};

export default Orders;
